/** Every league you can see: yours first */
import { useEffect, useState } from 'preact/hooks';
import { api, signInUrl, type BotLeagueSummary, type LeagueSummary } from '../api';
import { artwork, divisionOf, Mask } from '../divisions';
import { useMe } from '../me';
import { Link } from '../router';
import { ago, ErrorBox, Loading } from '../ui';
import { BOT_MODES, BOT_STATUS, botLeagueAddress } from './discord-league';

export function phaseText(phase: string, week: number) {
	return phase === 'setup' ? 'Setting up' : phase === 'draft' ? 'Drafting' : phase === 'season' ? `Week ${week}` : 'Finished';
}

export function phaseClass(phase: string) {
	return phase === 'draft' ? 'good' : phase === 'season' ? 'accent' : phase === 'setup' ? 'warn' : '';
}

export function LeaguesPage() {
	const me = useMe();
	const [leagues, setLeagues] = useState<LeagueSummary[] | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [botLeagues, setBotLeagues] = useState<BotLeagueSummary[]>([]);
	useEffect(() => {
		if (!me) return;
		api.get<{ leagues: LeagueSummary[] }>('/api/leagues').then(result => setLeagues(result.leagues), setError);
		api.get<{ leagues: BotLeagueSummary[] }>('/api/bot-leagues').then(result => setBotLeagues(result.leagues), () => setBotLeagues([]));
	}, [!!me, me?.user?.id, me?.user?.showdown?.id]);
	const botCard = (l: BotLeagueSummary) => {
		const [statusText, statusClass] = BOT_STATUS[l.status] || [l.status, ''];
		const division = divisionOf(l);
		return <Link key={`discord:${l.id}`} href={botLeagueAddress(l)} class={division ? 'card division-card' : 'card'}
			style="display:block;text-decoration:none;color:inherit">
			{division && <img class="division-card-art" src={artwork(division)} alt="" loading="lazy"
				onError={event => (event.currentTarget as HTMLImageElement).classList.add('missing')} />}
			{division && <Mask class="division-card-mask" division={division} />}
			<div class="row" style="justify-content:space-between">
				<h3 style="margin:0">{l.name}</h3>
				<span class={`badge ${statusClass}`}>{statusText}</span>
			</div>
			<p class="muted small">{BOT_MODES[l.mode] || l.mode} · {l.pools.reduce((n, p) => n + p.players, 0)} players
				{l.pools.length > 1 && ` in ${l.pools.length} pools`} · {l.budget} points</p>
			<p class="small">{l.you && <span class="badge good">You're playing</span>}{' '}{l.private && <><span class="badge">Private</span>{' '}</>}
				<span class="muted">{l.finishedAt ? `Finished ${ago(l.finishedAt)}` : l.startedAt ? `Started ${ago(l.startedAt)}` : `Set up ${ago(l.createdAt)}`}</span></p>
		</Link>;
	};

	// a Discord league's copy for the Draft Planner is left out: the league itself is listed (and lives at the copy's address)
	const shown = leagues?.filter(l => !l.botLeague) || [];
	const yours = shown.filter(l => l.role !== 'viewer');
	const others = shown.filter(l => l.role === 'viewer');
	const listedBot = botLeagues.filter(l => l.status !== 'cancelled');
	const yoursBot = listedBot.filter(l => l.you);
	const othersBot = listedBot.filter(l => !l.you);
	const card = (l: LeagueSummary) => <Link key={l.roomid} href={`/league/${l.roomid}`} class="card" style="display:block;text-decoration:none;color:inherit">
		<div class="row" style="justify-content:space-between">
			<h3 style="margin:0">{l.title}</h3>
			<span class={`badge ${phaseClass(l.phase)}`}>{phaseText(l.phase, l.week)}</span>
		</div>
		<p class="muted small">{l.format.split('@@@')[0]!.replace(/^\/challenge\s+/i, '')} · {l.players} players · {l.type === 'points' ? 'points draft' : 'free draft'}</p>
		<p class="small">
			{l.role === 'staff' && <span class="badge accent">You run it</span>}{' '}
			{l.you && <span class="badge good">You're playing</span>}{' '}
			{l.isPrivate && <span class="badge">Private</span>}{' '}
			<span class="muted">Updated {ago(l.updatedAt)}</span>
		</p>
	</Link>;

	return <>
		<div class="row" style="justify-content:space-between">
			<h1>Leagues</h1>
			{me?.user && <span class="row">
				{(me.user.isAdmin || me.user.isModerator) && <Link href="/admin/linking" class="button">Who hasn't linked</Link>}
				<Link href="/league/new" class="button primary">{me.user.isAdmin || me.user.isModerator ? 'Make a league' : 'Host a league'}</Link>
			</span>}
		</div>
		{me && !me.user && <p class="notice">You're seeing public leagues. <a href={signInUrl()}>Sign in</a> to see your own
			(including private ones) and play in them.</p>}
		{me?.user && !me.user.showdown && <p class="notice">Link your Showdown name on <Link href="/account">your account
			page</Link> to see the leagues you're in.</p>}
		{error ? <ErrorBox error={error} /> : !leagues ? <Loading /> : <>
			{yours.length + yoursBot.length > 0 && <><h2>Your leagues</h2><div class="grid">{yoursBot.map(botCard)}{yours.map(card)}</div></>}
			{others.length > 0 && <><h2>{yours.length + yoursBot.length ? 'Other Showdown leagues' : 'Showdown leagues'}</h2>
				<div class="grid">{others.map(card)}</div></>}
			{othersBot.length > 0 && <>
				<h2>{yours.length + yoursBot.length ? 'Other Discord leagues' : 'Discord leagues'}</h2>
				<p class="muted small">Drafted with the OCDL bot on Discord.</p>
				<div class="grid">{othersBot.map(botCard)}</div>
			</>}
			{!yours.length && !yoursBot.length && !others.length && !othersBot.length && <p class="empty">No leagues yet.</p>}
			<p class="muted small">Showdown leagues are set up on Showdown: room staff type <code>/draft setup</code> in their room.
				Discord leagues are set up with the bot's <code>/setup</code>, or here by the site's staff.</p>
		</>}
	</>;
}
